import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { logEvent } from "../analytics/analytics";

const STORAGE_KEY = "cart_v1";
const CartContext = createContext(undefined);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {}
  }, [items]);

  useEffect(() => {
    try {
      logEvent("cart", "cart_change", JSON.stringify(items));
    } catch {}
  }, [items]);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) {
        try {
          setItems(e.newValue ? JSON.parse(e.newValue) : []);
        } catch {}
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const addItems = (pkgs) => {
    setItems((prev) => {
      const map = new Map(prev.map((it) => [it.id, it]));

      for (const p of pkgs) {
        const curr = map.get(p.id);
        if (curr) {
          map.set(p.id, {
            ...curr,
            quantity: (curr.quantity || 1) + 1,
            lastAddedAt: Date.now(),
          });
        } else {
          map.set(p.id, {
            ...p,
            quantity: 1,
            lastAddedAt: Date.now(),
          });
        }
      }

      return Array.from(map.values());
    });
  };

  const increment = (product) => {
    setItems((prev) => {
      const idx = prev.findIndex((x) => x.id === product.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = {
          ...next[idx],
          quantity: (next[idx].quantity || 1) + 1,
          lastAddedAt: Date.now(),
        };
        return next;
      }

      return [...prev, { ...product, quantity: 1, lastAddedAt: Date.now() }];
    });
  };

  const decrementOrRemove = (id) => {
    setItems((prev) => {
      const idx = prev.findIndex((x) => x.id === id);
      if (idx === -1) return prev;

      const current = prev[idx];
      const newQty = (current.quantity || 1) - 1;

      if (newQty < 1) {
        return prev.filter((x) => x.id !== id);
      }

      const next = [...prev];
      next[idx] = { ...current, quantity: newQty };
      return next;
    });
  };

  const removeItem = (id) => setItems((prev) => prev.filter((it) => it.id !== id));

  const clear = () => {
    setItems([]);
    localStorage.removeItem(STORAGE_KEY);
  };

  const itemCount = useMemo(() => items.reduce((sum, it) => sum + (it.quantity || 1), 0), [items]);

  const value = useMemo(
    () => ({
      items,
      itemCount,
      increment,
      decrementOrRemove,
      removeItem,
      addItems,
      clear,
    }),
    [items, itemCount],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
